// Distinguishes the abort branch from a normal resolved value.
class AbortedMarker {}

const createAbortError = () => {
  return new DOMException("Operation aborted", "AbortError");
};

// Races one async step against an AbortSignal. Cancellation is cooperative:
// the underlying work may keep running, but callers stop awaiting its result.
const raceWithAbortSignal = async <T>(
  signal: AbortSignal,
  value: Promise<T> | (() => Promise<T>),
): Promise<T> => {
  if (signal.aborted) {
    throw createAbortError();
  }

  let removeAbortListener = (): void => {};
  // Resolve with a marker instead of rejecting so the race can decide in one
  // place whether the abort won, without mixing it with real async failures.
  const abortPromise = new Promise<AbortedMarker>((resolve) => {
    const onAbort = (): void => resolve(new AbortedMarker());
    signal.addEventListener("abort", onAbort, { once: true });
    removeAbortListener = (): void =>
      signal.removeEventListener("abort", onAbort);
  });

  const unwrapValue = () => {
    return typeof value === "function" ? value() : value;
  };

  let result: T | AbortedMarker;

  try {
    result = await Promise.race([abortPromise, unwrapValue()]);
  } catch (error) {
    removeAbortListener();
    throw error;
  }

  removeAbortListener();

  if (result instanceof AbortedMarker) {
    throw createAbortError();
  }

  return result;
};

export { raceWithAbortSignal };
